import { ENVIRONMENT } from '@/environments/environment.ts';
import { showToast } from '@/lib/use-sonner.tsx';
import { IStepProgress, UserImportProgress } from '@/model/Notification';
import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const NotificationContext = React.createContext<{
    openDialogProgress: boolean;
    setOpenDialogProgress: React.Dispatch<React.SetStateAction<boolean>>;
    setFunctionToReload: React.Dispatch<React.SetStateAction<any>>;
    connection: HubConnection;
    connect: () => Promise<void>;
    stepProgress: IStepProgress[];
    importationSummary: UserImportProgress | null;
    setImportationSummary: React.Dispatch<React.SetStateAction<UserImportProgress | null>>;
}>({
    connection : null,
    openDialogProgress : false,
    setOpenDialogProgress : null,
    connect : null,
    stepProgress : null,
    setFunctionToReload : undefined,
    importationSummary : null,
    setImportationSummary : null,
});

export function NotificationProvider({ children }: {children: React.ReactNode}) {
    const [openDialogProgress, setOpenDialogProgress] = useState(false);
    const [stepProgress, setStepProgress] = useState<IStepProgress[]>([]);
    const [connection, setConnection] = useState<HubConnection>(null);
    const [importationSummary, setImportationSummary] = useState<UserImportProgress | null>(null);
    const functionToReloadRef = useRef<any>(undefined);
    const { t } = useTranslation();

    const join = async () => {
        if (connection) await connection.stop();
        await connectHub(
                setConnection,
                setStepProgress,
                setOpenDialogProgress,
                setImportationSummary,
                t,
        );
    };

    return (
            <NotificationContext.Provider
                    value={{
                        openDialogProgress,
                        setOpenDialogProgress,
                        connection,
                        connect : join,
                        stepProgress,
                        setFunctionToReload : (fn) => functionToReloadRef.current = fn,
                        importationSummary,
                        setImportationSummary,
                    }}
            >
                {children}
            </NotificationContext.Provider>
    );
}

export function useNotificationContext() {
    return useContext(NotificationContext);
}

const setupSignalRListeners = async (
        connection: HubConnection,
        setStepProgress: React.Dispatch<React.SetStateAction<IStepProgress[]>>,
        setOpenDialogProgress: React.Dispatch<React.SetStateAction<boolean>>,
        setImportationSummary: React.Dispatch<React.SetStateAction<UserImportProgress | null>>,
        t: (key: string | string[], options?: any) => string,
) => {

    connection.onclose((error) => {
        if (error) {
            console.log('(SignalR) Disconnected. error:', error);
        } else {
            console.log('(SignalR) Disconnected.');
        }
    });

    connection.on('UserConnected', (confirmation: string) => {
        console.log(confirmation);
    });

    connection.on('UserImportProgress', (progress: string) => {
        const progressObj: UserImportProgress = JSON.parse(progress, (key, value) => {
            if (key.toLowerCase() === 'errors' && value === null) {
                return [];
            }
            return value;
        });

        const rawData = JSON.parse(progress);

        if (rawData.BlockedRecords && !progressObj.BlockedRecords) {
            progressObj.BlockedRecords = rawData.BlockedRecords;
        }

        if (progressObj.Errors && progressObj.Errors.length > 0) {
            const errorsAgg = progressObj.Errors.reduce((acc, error) => {
                const message = t(error.Message);
                return acc + (error.Row && error.Column ? `Row: ${error.Row}, Column: ${error.Column} - ${message}` : `${message}`) + '\n';
            }, '');

            setOpenDialogProgress(false);
            showToast({
                title : 'UserImport.Error',
                description : errorsAgg,
                variant : 'destructive',
            });
        } else {
            setOpenDialogProgress(!progressObj.IsFinished);
            setStepProgress((prevSteps) => {
                const newStep = {
                    Step : progressObj.ProcessedRecords.length,
                    TotalSteps : progressObj.TotalRecords,
                    Title : 'UserImport.Title',
                    RemainingJobsCount : 0,
                    Subtitle : 'UserImport.Subtitle',
                    IsFinished : progressObj.IsFinished,
                    CorrelationId : progressObj.CorrelationId,
                    CreatedOn : progressObj.CreatedOn,
                    IsError : progressObj.PendingRecords.length > 0,
                };
                return [...prevSteps, newStep];
            });
            if (progressObj.IsFinished) {
                
                setImportationSummary(progressObj);
                
                const successCount = progressObj.ProcessedRecords?.length - progressObj.Errors?.length || 0;
                const errorCount = progressObj.Errors?.length || 0;

                showToast({
                    title : t('UserImport.FinishedTitle'),
                    description : t('UserImport.FinishedDescription', { success : successCount, error : errorCount }),
                    variant : errorCount > 0 ? 'destructive' : 'success',
                    duration : 30000,
                    action : {
                        label : t('UserImport.ViewDetails'),
                        onClick : () => {
                        },
                    },
                });
            }

        }
    });

    await connection.invoke('ConnectHub');
};

const connectHub = async (
        setConnection: React.Dispatch<React.SetStateAction<HubConnection>>,
        setStepProgress: React.Dispatch<React.SetStateAction<IStepProgress[]>>,
        setOpenDialogProgress: React.Dispatch<React.SetStateAction<boolean>>,
        setImportationSummary: React.Dispatch<React.SetStateAction<UserImportProgress | null>>, // Receber o setter
        t: (key: string | string[], options?: any) => string,
) => {
    try {
        const negotiationUrl = `${ENVIRONMENT.authApiPath.basePath}/notification-hub`;

        console.log(`(SignalR) Starting connection... Url: ${negotiationUrl}`);

        const connection =
                new HubConnectionBuilder()
                        .withUrl(
                                negotiationUrl,
                                {
                                    transport : HttpTransportType.LongPolling,
                                    withCredentials : true,
                                },
                        )
                        .withAutomaticReconnect()
                        .configureLogging(LogLevel.Information)
                        .build();

        setConnection(connection);

        connection.onreconnected(async () => {
            await setupSignalRListeners(
                    connection,
                    setStepProgress,
                    setOpenDialogProgress,
                    setImportationSummary,
                    t,
            );
        });

        await connection.start().then(async () => {
            await setupSignalRListeners(
                    connection,
                    setStepProgress,
                    setOpenDialogProgress,
                    setImportationSummary,
                    t,
            );
        });
    } catch (e) {
        console.error(e);
    }
};
